<!--
SPDX-FileCopyrightText: 2023 Marlon W (Mawoka)

SPDX-License-Identifier: MPL-2.0
-->

<script lang="ts">
	import '@fontsource/marck-script/index.css';
	import { getLocalization } from '$lib/i18n';
	import { signedIn, pathname } from '$lib/stores';
	import { createTippy } from 'svelte-tippy';
	import Button from '$lib/ui/Button.svelte';
	import { browser } from '$app/environment';
	import ThemeSwitcher from '$lib/ThemeSwitcher.svelte';
	import { beforeNavigate } from '$app/navigation';
	import { draw, slide } from 'svelte/transition';

	const tippy = createTippy({
		arrow: true,
		animation: 'perspective-subtle',
		placement: 'bottom'
	});

	const { t } = getLocalization();

	let menuIsClosed = $state(true);
	const toggleMenu = () => {
		menuIsClosed = !menuIsClosed;
	};

	beforeNavigate(() => {
		menuIsClosed = true; // Closes menu to let the user see the page beneath
	});


</script>

<nav class="navbar-container w-screen px-4 lg:px-10 py-2 fixed backdrop-blur-2xl z-30 top-0">
	<!-- Desktop navbar -->
	<div class="hidden lg:flex lg:items-center lg:flex-row lg:justify-between">
		<div class="lg:flex lg:items-center lg:flex-row gap-1">
			<a
				href="/"
				class="navbar-logo font-black tracking-tight text-xl lg:text-2xl marck-script link-hover px-3 lg:px-5"
				>ClassQuiz</a
			>
			<a class="btn-nav border-2 rounded-sm" href="/play" aria-current={$pathname === "/play" ? "page" : undefined}>{$t('words.play')}</a>
			<a class="btn-nav" href="/explore" aria-current={$pathname === "/explore" ? "page" : undefined}>{$t('words.explore')}</a>
			<a class="btn-nav" href="/search" aria-current={$pathname === "/search" ? "page" : undefined}>{$t('words.search')}</a>
			{#if $signedIn}
				<a class="btn-nav" href="/dashboard" aria-current={$pathname === "/dashboard" ? "page" : undefined}>{$t('words.dashboard')}</a>
			{/if}
		</div>
		<div class="lg:flex lg:items-center lg:flex-row gap-1">
			{#if $signedIn}
				<a class="btn-nav" href="/api/v1/users/logout">{$t('words.logout')}</a>
			{:else}
				{#if !import.meta.env.VITE_REGISTRATION_DISABLED}
					<a class="btn-nav" href="/account/register">{$t('words.register')}</a>
				{/if}

				<a class="btn-nav" href="/account/login?returnTo={$pathname}">{$t('words.login')}</a
				>
			{/if}

			<div class="fit-content flex items-center justify-center gap-2">
				<div class="lg:flex items-center justify-center">
					<ThemeSwitcher />
				</div>
			</div>
		</div>
	</div>

	<!-- Mobile navbar -->
	<div class="lg:hidden">
		<!-- Navbar header -->
		<div class="flex items-center justify-between">
			<a
				href="/"
				class="navbar-logo font-black tracking-tight text-xl lg:text-2xl marck-script link-hover px-3 lg:px-5"
				>ClassQuiz</a
			>
			<a class="btn-nav flex" href="/play">{$t('words.play')}</a>

			<!-- Dark/Light mode toggle + Open/Close menu -->
			<div class="flex items-center">
				<ThemeSwitcher />

				{#if menuIsClosed}
					<button
						class="nav-hamburger px-3"
						id="open-menu"
						onclick={toggleMenu}
						aria-label={$t('words.open_menu')}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M3 6h18M3 12h18M3 18h18" />
						</svg>
					</button>
				{:else}
					<button
						class="nav-hamburger px-3"
						id="close-menu"
						onclick={toggleMenu}
						aria-label={$t('words.close_menu')}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
							><path in:draw|global={{ duration: 300 }} d="M18 6 6 18" /><path
								in:draw|global={{ duration: 300 }}
								d="m6 6 12 12"
							/></svg
						>
					</button>
				{/if}
			</div>
		</div>

		<!-- Navbar content -->
		{#if !menuIsClosed}
			<div class="flex flex-col" transition:slide|global={{ duration: 400 }}>
				<a class="btn-nav" href="/explore">{$t('words.explore')}</a>
				<a class="btn-nav" href="/search">{$t('words.search')}</a>
				{#if $signedIn}
					<a class="btn-nav" href="/dashboard">{$t('words.dashboard')}</a>
				{/if}

				<hr class="my-1" style="border-color: var(--theme-border);" />
				{#if $signedIn}
					<a class="btn-nav" href="/api/v1/users/logout">{$t('words.logout')}</a>
				{:else}
					{#if !import.meta.env.VITE_REGISTRATION_DISABLED}
						<a class="btn-nav" href="/account/register">{$t('words.register')}</a>
					{/if}

					<a class="btn-nav" href="/account/login?returnTo={$pathname}"
						>{$t('words.login')}</a
					>
				{/if}

				<div class="fit-content flex items-center justify-center my-2">
				</div>
			</div>
		{/if}
	</div>
</nav>

<style>
	.navbar-container {
		background-color: var(--theme-surface);
		box-shadow: var(--shadow-md);
	}
	.navbar-logo {
		color: var(--theme-text);
	}
	.nav-hamburger {
		color: var(--theme-text);
		border-radius: var(--radius-md, 0.375rem);
	}
	.nav-hamburger:focus-visible {
		outline: 2px solid var(--theme-ring, var(--theme-primary));
		outline-offset: 2px;
	}
</style>
